import React, { useState } from 'react';
import { Button, ButtonGroup, Center, Spinner } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { ColumnType } from 'src/components/molecules';
import { PaginatedTable } from 'src/components/organisms';
import { useGetCaseImporterJobDetailsQuery } from 'src/graphql';
import { getReadableId } from 'src/types';
import { ImportReportDetailsContainer } from './ImportReportDetails.styles';

interface ImportReportDetailsProps {
  jobId: string;
  isLoading?: boolean;
}

const ITEMS_PER_PAGE = 10;

enum TabOption {
  IMPORTED = 'Imported cases',
  FAILED = 'Failed to import',
}

export const ImportReportDetails: React.FC<ImportReportDetailsProps> = ({
  jobId,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabOption>(TabOption.IMPORTED);
  const [importedCasesPage, setImportedCasesPage] = useState(1);
  const [failedCasesPage, setFailedCasesPage] = useState(1);

  const { data: jobDetailsData, isLoading } = useGetCaseImporterJobDetailsQuery(
    { jobId },
  );

  const importedCases =
    jobDetailsData?.getCaseImporterJobDetails?.importedCases ?? [];
  const failedCases =
    jobDetailsData?.getCaseImporterJobDetails?.failedCases ?? [];
  const hasBothTabs = importedCases.length > 0 && failedCases.length > 0;

  const importedCasesColumns: ColumnType[] = [
    { label: 'Case Name', value: 'caseName' },
    { label: 'Original Case ID', value: 'originalCaseId' },
    { label: 'Case ID', value: 'caseId' },
    { label: 'Action', value: 'action' },
  ];

  const failedCasesColumns: ColumnType[] = [
    { label: 'Case Name', value: 'caseName' },
    { label: 'Original Case ID', value: 'originalCaseId' },
    { label: 'Error', value: 'error' },
  ];

  const importedCasesTableData = importedCases.map((caseItem) => {
    return {
      row: String(caseItem.id),
      cellData: [
        caseItem.name ?? 'Untitled Case',
        caseItem.importedId || '-',
        caseItem.readableId || getReadableId(caseItem.id),
        <Button
          key={`view-case-${caseItem.id}`}
          variant="tertiary"
          noPadding
          noMinWidth
          onClick={() => {
            window.open(`/case?id=${caseItem.id}`, '_blank');
          }}
          data-testid={`view-case-${caseItem.id}`}
        >
          <StyledIcon className="uil uil-file-alt" size="16px" />
          View case
        </Button>,
      ],
    };
  });

  const failedCasesTableData = failedCases.map((caseItem) => {
    return {
      row: String(caseItem.id),
      cellData: [
        caseItem.name ?? 'Untitled Case',
        caseItem.importedId || '',
        caseItem.error ?? 'Unknown error',
      ],
    };
  });

  const importedCasesNumPages = Math.ceil(
    importedCases.length / ITEMS_PER_PAGE,
  );
  const failedCasesNumPages = Math.ceil(failedCases.length / ITEMS_PER_PAGE);

  const handleTabChange = (option: TabOption) => {
    setSelectedTab(option);
    setImportedCasesPage(1);
    setFailedCasesPage(1);
  };

  if (isLoading) {
    return (
      <ImportReportDetailsContainer>
        <Center>
          <Spinner variant="secondary" size="large" />
        </Center>
      </ImportReportDetailsContainer>
    );
  }

  const showImportedTable =
    (hasBothTabs && selectedTab === TabOption.IMPORTED) ||
    (!hasBothTabs && importedCases.length > 0);
  const showFailedTable =
    (hasBothTabs && selectedTab === TabOption.FAILED) ||
    (!hasBothTabs && failedCases.length > 0);
  const showEmptyState =
    !hasBothTabs && importedCases.length === 0 && failedCases.length === 0;

  return (
    <ImportReportDetailsContainer>
      {hasBothTabs && (
        <ButtonGroup
          options={[TabOption.IMPORTED, TabOption.FAILED]}
          selectedOption={selectedTab}
          setSelectedOption={handleTabChange}
          buttonGroupOptionTestId="import-report-tabs"
          size="small"
        />
      )}
      {showImportedTable && (
        <PaginatedTable
          columns={importedCasesColumns}
          tableData={importedCasesTableData}
          visibleColumns={importedCasesColumns}
          data-testid="imported-cases-table"
          isFullWidth={true}
          borderRadius="12px"
          fixedRowHeight={true}
          rowHeight={72}
          emptyText="No imported cases"
          maxRows={ITEMS_PER_PAGE}
          numPages={importedCasesNumPages}
          handleClickPage={setImportedCasesPage}
          currentPage={importedCasesPage}
          showPagination={importedCases.length > 0}
        />
      )}
      {showFailedTable && (
        <PaginatedTable
          columns={failedCasesColumns}
          tableData={failedCasesTableData}
          visibleColumns={failedCasesColumns}
          data-testid="failed-cases-table"
          isFullWidth={true}
          borderRadius="12px"
          fixedRowHeight={true}
          rowHeight={72}
          emptyText="No failed cases"
          maxRows={ITEMS_PER_PAGE}
          numPages={failedCasesNumPages}
          handleClickPage={setFailedCasesPage}
          currentPage={failedCasesPage}
          showPagination={failedCases.length > 0}
        />
      )}
      {showEmptyState && (
        <PaginatedTable
          columns={importedCasesColumns}
          tableData={[]}
          visibleColumns={importedCasesColumns}
          data-testid="empty-cases-table"
          isFullWidth={true}
          borderRadius="12px"
          fixedRowHeight={true}
          rowHeight={72}
          emptyText="No cases available"
          maxRows={ITEMS_PER_PAGE}
          showPagination={false}
        />
      )}
    </ImportReportDetailsContainer>
  );
};
